import { normalizeShopifyId } from './storewide-sale';

export const FREE_GIFT_LINE_ATTRIBUTE_KEY = '_free_gift';
export const FREE_GIFT_PROMOTION_ID_ATTRIBUTE_KEY = '_free_gift_promotion_id';
// Legacy global dismissal key — treated as inert and cleared when encountered.
export const FREE_GIFT_DISMISSED_ATTRIBUTE_KEY = '__free_gift_dismissed';

export const isFreeGiftLine = (lineItem) => (
  lineItem?.attributes?.some(({ key, value }) => (
    key === FREE_GIFT_LINE_ATTRIBUTE_KEY && value === 'true'
  )) ?? false
);

export const getGiftPromotionId = (lineItem) => (
  lineItem?.attributes?.find(({ key }) => (
    key === FREE_GIFT_PROMOTION_ID_ATTRIBUTE_KEY
  ))?.value ?? null
);

/**
 * Stable per-promotion identity. Prefers discountId; falls back to giftVariantId
 * for cached legacy API payloads that predate discountId.
 */
export const getPromotionKey = (promotion) => (
  normalizeShopifyId(promotion?.discountId ?? promotion?.giftVariantId)
);

export const dismissedKeyForPromotion = (promotion) => {
  const key = getPromotionKey(promotion);
  return key ? `__free_gift_dismissed_${key}` : null;
};

export const findAllFreeGiftLines = (cart) => (
  cart?.lineItems?.nodes?.filter(isFreeGiftLine) ?? []
);

/**
 * Prefer promo-id attribute match. Fall back to gift variant id only for
 * pre-deploy gift lines that have no promo-id attribute.
 */
export const findFreeGiftLineForPromotion = (cart, promotion) => {
  if (!cart?.lineItems?.nodes || !promotion) {
    return null;
  }

  const promotionKey = getPromotionKey(promotion);
  const giftVariantId = normalizeShopifyId(promotion.giftVariantId);

  return cart.lineItems.nodes.find((lineItem) => {
    if (!isFreeGiftLine(lineItem)) {
      return false;
    }

    const linePromotionId = getGiftPromotionId(lineItem);
    if (linePromotionId) {
      return normalizeShopifyId(linePromotionId) === promotionKey;
    }

    // Legacy gift line without a promo-id attribute
    return normalizeShopifyId(lineItem.variant?.shopifyId) === giftVariantId;
  }) ?? null;
};

/** @deprecated Prefer findFreeGiftLineForPromotion / findAllFreeGiftLines */
export const findFreeGiftLine = (cart) => (
  cart?.lineItems?.nodes?.find(isFreeGiftLine) ?? null
);

const toNormalizedIdSet = (ids = []) => (
  new Set(ids.map(normalizeShopifyId).filter(Boolean))
);

const getProductCollections = (product) => {
  if (product?.collections?.nodes) {
    return product.collections.nodes;
  }

  return product?.collections ?? [];
};

export const isLineItemEligibleForFreeGift = (promotion, lineItem) => {
  if (!promotion || !lineItem) {
    return false;
  }

  if (promotion.appliesToAll) {
    return true;
  }

  const variant = lineItem.variant;
  const product = variant?.product;

  if (!variant || !product) {
    return false;
  }

  const variantIdSet = toNormalizedIdSet(promotion.variantIds);
  if (variantIdSet.size > 0) {
    return variantIdSet.has(normalizeShopifyId(variant.shopifyId));
  }

  const productId = normalizeShopifyId(product.shopifyId);
  if (productId && toNormalizedIdSet(promotion.productIds).has(productId)) {
    return true;
  }

  const collectionIdSet = toNormalizedIdSet(promotion.collectionIds);
  if (collectionIdSet.size === 0) {
    return false;
  }

  return getProductCollections(product).some((collection) => (
    collectionIdSet.has(normalizeShopifyId(collection.shopifyId ?? collection.id))
  ));
};

export const getCartSubtotalExcludingGift = (cart) => (
  cart?.lineItems?.nodes?.reduce((total, lineItem) => (
    isFreeGiftLine(lineItem) ? total : (
      total + parseFloat(lineItem.estimatedCost.subtotalAmount.amount)
    )
  ), 0) ?? 0
);

export const getQualifyingSubtotal = (cart, promotion = null) => {
  if (!cart?.lineItems?.nodes) {
    return 0;
  }

  if (!promotion || promotion.appliesToAll) {
    return getCartSubtotalExcludingGift(cart);
  }

  return cart.lineItems.nodes.reduce((total, lineItem) => {
    if (isFreeGiftLine(lineItem)) {
      return total;
    }

    if (!isLineItemEligibleForFreeGift(promotion, lineItem)) {
      return total;
    }

    return total + parseFloat(lineItem.estimatedCost.subtotalAmount.amount);
  }, 0);
};

export const cartHasQualifyingProduct = (cart, promotion) => {
  if (!promotion) {
    return false;
  }

  if (promotion.appliesToAll) {
    return true;
  }

  return cart?.lineItems?.nodes?.some((lineItem) => (
    !isFreeGiftLine(lineItem) && isLineItemEligibleForFreeGift(promotion, lineItem)
  )) ?? false;
};

export const isGiftLineTotalZero = (lineItem) => (
  parseFloat(lineItem.estimatedCost.totalAmount.amount) === 0
);

export const isFreeGiftDismissedForPromotion = (cart, promotion) => {
  const dismissedKey = dismissedKeyForPromotion(promotion);
  if (!dismissedKey) {
    return false;
  }

  return cart?.attributes?.some(({ key, value }) => (
    key === dismissedKey && value === 'true'
  )) ?? false;
};

/** @deprecated Prefer isFreeGiftDismissedForPromotion */
export const isFreeGiftDismissed = (cart) => (
  cart?.attributes?.some(({ key, value }) => (
    key === FREE_GIFT_DISMISSED_ATTRIBUTE_KEY && value === 'true'
  )) ?? false
);

/**
 * Coerce API response to a promotions array.
 * Supports the new `{ promotions }` shape and the cached legacy `{ promotion }` shape.
 */
export const coerceFreeGiftPromotions = (data) => {
  if (Array.isArray(data?.promotions)) {
    return data.promotions;
  }

  if (data?.promotion) {
    return [data.promotion];
  }

  return [];
};

export const getFreeGiftPromoState = ({ isError, isSuccess, data }) => {
  if (isError) {
    return 'unknown';
  }

  if (!isSuccess) {
    return 'unknown';
  }

  return coerceFreeGiftPromotions(data).length > 0 ? 'active' : 'inactive';
};
